import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BfAuthService } from '@brafton/skynet-angular-security-service';

import { HOME_ROUTE } from '../../core/skynet-auth.config';

/**
 * Login page. It renders only a backdrop: Auth0 Lock, opened by `BfAuthService.login()`
 * on init, is the whole login UI. Lock is configured `closable: false`, so it cannot be
 * dismissed into a dead page — which is why there is no card, no button and no error
 * panel here. Lock reports its own errors in its own widget. Mirrors atlas-app-v2's auth
 * landing page.
 *
 * This is a **redirect** flow, so `login()` eventually navigates away from the app rather
 * than resolving: Auth0 returns to the app origin with the tokens in the URL fragment,
 * and the root component's `handleAuthentication()` listener stores them and routes on.
 * Nothing sequenced after `login()` should assume it still runs.
 *
 * `BfAuthService.login()` deliberately no-ops when the URL already carries a token (the
 * post-redirect callback) or Lock has already reported success, so calling it on init
 * neither loops nor strands the visitor.
 */
@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.html',
  styleUrl: './login-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPage implements OnInit {
  private readonly auth = inject(BfAuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  ngOnInit(): void {
    // Landed here with a live session (e.g. navigated directly) — skip Lock and go
    // wherever the guard wanted to send us.
    if (this.auth.authenticated()) {
      const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? HOME_ROUTE;
      void this.router.navigateByUrl(returnUrl);
      return;
    }

    this.auth.login();
  }
}
